import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';

/**
 * Frazy wskazujące że user sam anulował logowanie.
 * Zwracamy null (= cicho, bez Sentry, bez komunikatu).
 */
const CANCEL_PHRASES = [
  'cancelled by the user',
  'canceled by the user',
  'user canceled',
  'user cancelled',
  'popup_closed',
  'popup closed',
  'access_denied',
  'the user canceled the sign-in flow',
  'the operation couldn\'t be completed',
  'com.apple.authenticationservices.authorizationerror error 1001',
  'sign_in_cancelled',
  'canceled',
];

/**
 * Sprawdza czy błąd to anulowanie przez usera.
 */
function isCancellation(err) {
  const msg = (err?.message || String(err)).toLowerCase();
  const code = (err?.code || '').toLowerCase();
  return CANCEL_PHRASES.some(phrase => msg.includes(phrase) || code.includes(phrase));
}

/**
 * Centralny handler błędów autentykacji.
 * 
 * Zwraca null gdy:
 * - user sam anulował logowanie (nie wysyła do Sentry, nie pokazuje komunikatu)
 * - błąd walidacji 400 przy rejestracji (caller obsługuje fieldErrors)
 * 
 * Zwraca string z komunikatem gdy to prawdziwy błąd (wysyła do Sentry).
 * 
 * Użycie:
 *   catch (err) {
 *     const msg = handleAuthError(err, { ... });
 *     if (msg) {
 *       setError(msg);
 *       setSocialLoginFailed(true);
 *     }
 *   }
 */
export function handleAuthError(err, context) {
  // Anulowanie przez usera — cicho, bez Sentry, bez komunikatu
  if (isCancellation(err)) {
    return null;
  }

  const platform = Capacitor.getPlatform();

  const errorInfo = {
    method: context.method,
    step: context.step,
    platform,
    isNative: Capacitor.isNativePlatform(),
    statusCode: err?.response?.status || null,
    serverResponse: err?.response?.data || null,
    errorMessage: err?.message || String(err),
    errorName: err?.name || null,
  };

  // Sentry: exception z tagami i extra data
  Sentry.captureException(err, {
    tags: {
      auth_method: context.method,
      auth_step: context.step,
      platform: platform,
    },
    extra: errorInfo,
  });

  // --- User-facing messages w zależności od typu błędu ---

  // Brak sieci / timeout
  if (!err?.response && (err?.message?.toLowerCase().includes('network') || err?.code === 'ERR_NETWORK' || err?.message?.toLowerCase().includes('timeout'))) {
    return "Brak połączenia z internetem. Sprawdź sieć i spróbuj ponownie.";
  }

  const status = err?.response?.status;

  // 400 — błędy walidacji (rejestracja) — zwracamy null żeby caller mógł obsłużyć fieldErrors
  if (status === 400 && context.step === 'register') {
    return null;
  }

  // 401 — złe dane logowania
  if (status === 401) {
    return "Błędny adres email lub hasło.";
  }

  // 403 — konto zablokowane / brak dostępu
  if (status === 403) {
    return "Dostęp do konta został ograniczony. Skontaktuj się z nami: kontakt@matematly.pl";
  }

  // 409 — konflikt (np. konto już istnieje)
  if (status === 409) {
    return "Konto z tym adresem email już istnieje. Spróbuj się zalogować.";
  }

  // 429 — rate limit
  if (status === 429) {
    return "Zbyt wiele prób. Odczekaj chwilę i spróbuj ponownie.";
  }

  // 5xx — błąd serwera
  if (status && status >= 500) {
    return "Wystąpił nieoczekiwany błąd serwera. Spróbuj ponownie za chwilę lub skorzystaj z naszego formularza rejestracji.";
  }

  // Fallback
  return context.fallbackMessage || "Wystąpił nieoczekiwany błąd. Spróbuj ponownie.";
}

/**
 * Sprawdza brak tokenu po social login.
 * Jeśli brak result/result.result = user anulował → cicho, zwraca false.
 * Jeśli plugin zwrócił dane ale bez tokenu → prawdziwy problem, raportuje, zwraca true.
 * 
 * Zwraca true jeśli caller powinien pokazać błąd.
 * Zwraca false jeśli caller powinien cicho zignorować.
 */
export function reportMissingToken(method, result) {
  // Brak result lub brak result.result = user anulował, cicho
  if (!result || !result.result) {
    return false;
  }

  // Plugin zwrócił jakieś dane ale bez idToken — to prawdziwy problem
  const platform = Capacitor.getPlatform();

  Sentry.captureMessage(`Social login: brak idToken (${method})`, {
    level: 'warning',
    tags: {
      auth_method: method,
      auth_step: 'token_missing',
      platform: platform,
    },
    extra: {
      resultKeys: Object.keys(result),
      innerResultKeys: result.result ? Object.keys(result.result) : null,
      hasIdToken: !!(result?.result?.idToken),
      platform: platform,
    },
  });

  return true;
}